import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from 'echarts/components';
import {
  init,
  use as registerEChartsModules,
  type EChartsCoreOption,
  type EChartsType,
} from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useMemo, useRef } from 'react';
import { cn } from '@/shared/lib/cn';
import { applyChartMotionPreference, useReducedMotionPreference } from './chartMotion';

registerEChartsModules([
  BarChart,
  LineChart,
  PieChart,
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

interface EChartProps {
  option: EChartsCoreOption;
  ariaLabel: string;
  className?: string;
  onClick?: (params: unknown) => void;
  /** Zeiger über einem Datenpunkt (`params`) oder wieder heraus (`null`). */
  onHover?: (params: unknown) => void;
}

export function EChart({ option, ariaLabel, className, onClick, onHover }: EChartProps) {
  const host = useRef<HTMLDivElement>(null);
  const chart = useRef<EChartsType | null>(null);
  const reducedMotion = useReducedMotionPreference();
  const accessibleOption = useMemo(() => {
    const motion = applyChartMotionPreference(option, reducedMotion) as EChartsCoreOption & {
      aria?: Record<string, unknown>;
    };
    // Mit `aria` schreibt ECharts sonst seine eigene, englische Beschreibung über `ariaLabel`.
    return motion.aria
      ? { ...motion, aria: { ...motion.aria, label: { description: ariaLabel } } }
      : motion;
  }, [option, reducedMotion, ariaLabel]);

  useEffect(() => {
    if (!host.current) return;
    const instance = init(host.current, undefined, { renderer: 'canvas' });
    chart.current = instance;
    const resize = () => instance.resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    if (observer) observer.observe(host.current);
    else window.addEventListener('resize', resize);
    return () => {
      observer?.disconnect();
      if (!observer) window.removeEventListener('resize', resize);
      instance.dispose();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    chart.current?.setOption(accessibleOption, { notMerge: true });
  }, [accessibleOption]);

  useEffect(() => {
    const instance = chart.current;
    if (!instance || !onClick) return;
    instance.on('click', onClick);
    return () => {
      instance.off('click', onClick);
    };
  }, [onClick]);

  useEffect(() => {
    const instance = chart.current;
    if (!instance || !onHover) return;
    const leave = () => onHover(null);
    instance.on('mouseover', onHover);
    instance.on('mouseout', leave);
    return () => {
      instance.off('mouseover', onHover);
      instance.off('mouseout', leave);
    };
  }, [onHover]);

  return (
    <div
      ref={host}
      role="img"
      aria-label={ariaLabel}
      className={cn('min-h-72 w-full', className)}
    />
  );
}
